import { useCallback, useMemo, useState } from 'react'

import {
  inlineEditCount,
  isSameInlineValue,
  pendingInlineRows,
  type InlineEdits,
  type InlineEditSpec,
  type InlineEditValue,
} from '@/lib/inlineEdit'

/**
 * Holds the cells a user has edited in a list but not yet saved.
 *
 * A cell that is edited back to the value it started with stops counting as a
 * change, and a row with no changed cells left drops out entirely — so the
 * save bar disappears when the user undoes their own edits by hand.
 */
export function useInlineEdits<TData>(
  getRowId: (row: TData) => string = (row) => (row as Record<string, unknown>).id as string,
) {
  const [edits, setEdits] = useState<InlineEdits<TData>>({})

  const setEdit = useCallback(
    (row: TData, columnId: string, value: InlineEditValue, spec: InlineEditSpec<TData>) => {
      const rowId = getRowId(row)
      setEdits((current) => {
        const next = { ...current }
        const cells = { ...(next[rowId]?.cells ?? {}) }
        if (isSameInlineValue(value, spec.value(row))) {
          delete cells[columnId]
        } else {
          cells[columnId] = { value, patch: spec.patch(value, row) }
        }
        if (Object.keys(cells).length === 0) delete next[rowId]
        // The row travels with the edit so a save can read the fields the
        // patch was built from, even if the list has moved on since.
        else next[rowId] = { row, cells }
        return next
      })
    },
    [getRowId],
  )

  const reset = useCallback(() => setEdits({}), [])

  const pending = useMemo(() => pendingInlineRows(edits), [edits])
  const count = useMemo(() => inlineEditCount(edits), [edits])

  return {
    edits,
    setEdit,
    reset,
    /** One entry per touched row: its id, the row, and the whole payload. */
    pending,
    /** Number of staged cells. */
    count,
    /** Number of touched rows. */
    rowCount: pending.length,
    isDirty: count > 0,
  }
}
